import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShoppingBagIcon,
} from '@heroicons/react/20/solid';
import {
  Button,
  dobFormatter,
  lastScanDate,
  timestampFormatter,
} from '@retainerclub/common';
import PropTypes from 'prop-types';
import { useMemo } from 'react';

function DashboardPatientCard({
  patient,
  handleShop,
  handleOpenRetainerWizard,
}) {
  const scheduledShipments = useMemo(
    () =>
      [...patient.scheduledShipments]
        .sort(
          (a, b) =>
            new Date(b.expectedShippedAt) - new Date(a.expectedShippedAt),
        )
        .filter((shipment) => !shipment.consumed),
    [patient.scheduledShipments],
  );

  const activeSubscription = useMemo(
    () =>
      patient?.subscriptions?.find(
        (subscription) => subscription.state === 'active',
      ),
    [patient?.subscriptions],
  );

  function hasPlanOrFreeTrialText() {
    if (patient.hasFreeTrial) {
      return 'Free Trial';
    }
    if (patient.hasPlan) {
      return 'Smile Care Plan';
    }
    return 'No Current Plan';
  }

  return (
    <div className="basis-1 sm:basis-1/2 md:basis-1/3 lg:basis-1/4">
      <div className="m-2 flex h-full flex-col divide-y divide-gray-300 rounded-lg border bg-rcgray-50 text-center shadow-lg">
        <div className="flex flex-1 flex-row justify-between p-4 sm:flex-col">
          <div className="text-left sm:text-center">
            <h3 className="font-bold text-2xl">{patient.firstName}</h3>
            <p className="text-sm">
              dob:{' '}
              <span className="font-medium">{dobFormatter(patient.dob)}</span>
            </p>
            {lastScanDate(patient.scanSentAt, patient.partnerUploadScanAt) && (
              <p className="text-sm">
                Last scan:{' '}
                <span className="font-medium">
                  {timestampFormatter(
                    lastScanDate(
                      patient.scanSentAt,
                      patient.partnerUploadScanAt,
                    ),
                    false,
                  )}
                </span>
              </p>
            )}
            <div className="mt-2">
              <span
                className={`inline-flex items-center gap-2 rounded-full ${patient.hasPlan ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-yellow-50 text-yellow-800 ring-yellow-600/20'} ml-0 px-2 py-1 font-medium text-xs ring-1 ring-inset`}
              >
                {patient.hasPlan ? (
                  <CheckCircleIcon className="size-4" />
                ) : (
                  <ExclamationTriangleIcon className="size-4" />
                )}
                {hasPlanOrFreeTrialText()}
              </span>
              {activeSubscription && (
                <p className="mt-1 text-sm leading-4">
                  <span className="">Active {activeSubscription.name}</span>
                  <br />
                  <span className="text-xs">
                    renewing on{' '}
                    {timestampFormatter(activeSubscription.renewalAt, false)}
                  </span>
                </p>
              )}
            </div>
          </div>

          <div className="mt-2 flex flex-col items-center justify-center rounded-lg border border-rcgray-300 bg-rcgray-100 p-2 text-black/65">
            <p className="text-sm leading-4">
              <span className="">Retainer Bank</span>

              <br />

              <span className="font-bold text-base">
                {patient.retainerBank > 0 ? patient.retainerBank : 'none'}&nbsp;
                available
              </span>
              <br />
              {scheduledShipments.length > 0 && (
                <span className="text-xs">
                  (next expected to ship&nbsp;
                  {timestampFormatter(
                    scheduledShipments[0].expectedShippedAt,
                    false,
                  )}
                  )
                </span>
              )}
            </p>
            {patient.hasPlan && !patient.scanReady ? (
              <div className="mt-1 inline-flex items-center rounded-xl bg-gray-100 px-2 py-1 font-medium text-gray-700 text-sm ring-1 ring-gray-600/20 ring-inset">
                Waiting for Patient Scan
              </div>
            ) : patient.hasPlan &&
              patient.scanReady &&
              patient.hasPendingOrderWithoutProductionOrder ? (
              <div className="mt-1 inline-flex items-center rounded-xl bg-yellow-50 px-2 py-1 font-medium text-sm text-yellow-800 ring-1 ring-yellow-600/20 ring-inset">
                Order Processing...
              </div>
            ) : patient.hasPlan &&
              patient.scanReady &&
              !patient.hasPendingOrderWithoutProductionOrder &&
              patient.hasUnshippedProductionOrder ? (
              <div className="mt-1 inline-flex items-center rounded-xl bg-blue-50 px-2 py-1 font-medium text-blue-700 text-sm ring-1 ring-blue-600/20 ring-inset">
                Retainer in Production
              </div>
            ) : patient.hasPlan &&
              patient.scanReady &&
              !patient.hasPendingOrderWithoutProductionOrder &&
              !patient.hasUnshippedProductionOrder ? (
              <Button
                className="mt-1 rounded-xl"
                onClick={() => handleOpenRetainerWizard(patient)}
                name="Need A Retainer?"
                size="sm"
              />
            ) : (
              <Button
                className="mt-1 rounded-xl"
                onClick={() => handleShop(patient)}
                name={`Shop for ${patient.firstName}`}
                size="sm"
              />
            )}
          </div>
        </div>
        <div>
          <div className="-mt-px flex divide-x divide-gray-300">
            <div className="flex w-0 flex-1">
              <button
                type="button"
                onClick={() => handleShop(patient)}
                className="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 font-semibold text-rcgray-800 text-sm"
              >
                <ShoppingBagIcon
                  className="size-5 text-rcprimary-400"
                  aria-hidden="true"
                />
                Shop
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

DashboardPatientCard.propTypes = {
  patient: PropTypes.object.isRequired,
  handleShop: PropTypes.func.isRequired,
  handleOpenRetainerWizard: PropTypes.func.isRequired,
};

export default DashboardPatientCard;
